import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  APP_NAME,
  APP_SUPPORT_EMAIL,
  APP_URL,
  FULL_COMPANY_NAME,
  TAGLINE,
} from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import { APP_TERMS_ROUTE } from "../helpers/routeNames";
import SEO from "../components/SEO";

const APP_PRIVACY_URL = `https://${APP_URL}/privacy`;

export default function TermsOfService({ mustAcceptTos = false }) {
  return (
    <Container $mustAcceptTos={mustAcceptTos}>
      <SEO
        title={`Terms of Service | ${APP_NAME} - ${TAGLINE}`}
        description={`${APP_NAME} Terms of Service. Automated AI project scoping, two-sided procurement marketplace, search execution fees, contractor routing, and limitations of liability.`}
        keywords={`${APP_NAME} terms, AI scope disclaimer, procurement marketplace, search fee, contractor routing, no endorsement, limitation of liability`}
        imageAlt={`${APP_NAME} Terms of Service`}
        category="Legal"
        tags={[
          "Clumse terms of service",
          "AI diagnostic disclaimer",
          "procurement marketplace",
          "contractor routing terms",
        ]}
      />
      <MainHeader>Terms of Service</MainHeader>
      <Spacer $marginTop="2rem" />
      <TextContainer $mustAcceptTos={mustAcceptTos}>

        <TextSection>
          {APP_NAME} Services ("{APP_NAME}" or "Services") are provided by{" "}
          {FULL_COMPANY_NAME} ("We", "Our", "Company") to you ("User",
          "Homeowner", "Landlord", or "Contractor"), collectively referred to
          as "Users."
        </TextSection>
        <TextSection>
          By accessing or using any part of the {APP_NAME} platform, including
          our AI-powered intake chat, automated project scoping engine, vendor
          dispatch notification system, bid coordination tools, or any related
          product, you confirm that you have read, understood, and agree to be
          bound by these Terms of Service and our{" "}
          <a href={APP_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>{" "}
          (collectively, the "Agreement"). If you do not agree, you must
          discontinue use of the Services immediately.
        </TextSection>
        <TextSection>
          <strong>Last Updated:</strong> June 9, 2026
        </TextSection>

        {/* ── Section 1 ── */}
        <Header>1. Nature of Service: Automated Procurement Utility</Header>
        <TextSection>
          <strong>{APP_NAME} is an independent procurement utility platform,
          not a licensed contractor.</strong> Our Services are designed to
          streamline the process by which property owners and landlords obtain
          competitive bids from local, licensed independent tradesmen. We
          accomplish this by deploying a proprietary AI intake engine that
          collects project scope information through a conversational interface,
          compiles a pre-scoped contractor data packet, and transmits that
          packet to a network of geographically relevant, independent third-party
          vendors for competitive bidding purposes.
        </TextSection>
        <TextSection>
          {APP_NAME} is a two-sided marketplace connecting two independent
          participant classes:
        </TextSection>
        <List>
          <ListItem>
            <strong>Property Owners (Homeowners and Landlords):</strong> Individuals
            or entities seeking competitive bids on residential or investment
            property repairs, renovations, or maintenance work.
          </ListItem>
          <ListItem>
            <strong>Independent Contractors and Tradesmen:</strong> Licensed
            third-party service providers who receive automated dispatch
            notifications containing pre-scoped project data and submit
            competitive bids directly to property owners.
          </ListItem>
        </List>
        <TextSection>
          {APP_NAME} facilitates the connection between these two parties.
          We are not a party to any agreement, contract, work order, or
          transaction that may result from that connection.
        </TextSection>

        {/* ── Section 2 ── */}
        <Header>2. AI Diagnostic Disclaimer: "As-Is" Scope and Estimates</Header>
        <TextSection>
          <strong>Automated scopes are informational only.</strong> The project
          blueprints, structural assessments, pre-scoped work item lists, and
          local market cost range estimates generated by the {APP_NAME} AI
          intake engine are produced via machine learning algorithms that analyze
          conversational inputs, ZIP code market data, and historical project
          parameters. These outputs are provided strictly for informational and
          procurement coordination purposes.
        </TextSection>
        <TextSection>
          <strong>{APP_NAME} does not guarantee the absolute mathematical
          precision, physical accuracy, or field-level correctness of any
          AI-generated scope, task list, cost estimate, or structural
          assessment.</strong> Actual job site conditions, material costs,
          permit requirements, local code compliance obligations, and labor
          variables may differ materially from any automated output. It is the
          sole and ultimate responsibility of the contracting parties (the
          property owner and the selected contractor) to independently verify
          all actual job site conditions, confirm scope accuracy, and agree
          upon final pricing before any work commences.
        </TextSection>
        <TextSection>
          Any cost ranges displayed (e.g., "Local market cost range: $X to $Y")
          are statistical estimates based on aggregated regional market data
          and are not binding quotes, contractor bids, or warranty
          representations by {APP_NAME}. Actual contractor bids may vary
          significantly based on field conditions, material selection, and
          local labor rates.
        </TextSection>

        {/* ── Section 3 ── */}
        <Header>3. No Contractor Endorsement and Limitation of Liability</Header>
        <TextSection>
          <strong>{APP_NAME} does not endorse, certify, verify, employ,
          supervise, or guarantee the quality, licensure, insurance status,
          workmanship, or conduct of any contractor or tradesman to whom
          project data is routed via our dispatch network.</strong> Independent
          contractors are third-party service providers acting solely under
          their own licenses, credentials, bonding, insurance policies, and
          professional obligations.
        </TextSection>
        <TextSection>
          {APP_NAME} routes verified project data packets to local, third-party
          licensed independent tradesmen based on ZIP code matching and service
          category alignment. We are not a party to, and accept no liability
          arising from, any of the following:
        </TextSection>
        <List>
          <ListItem>
            Any contractual agreements, work orders, or service contracts
            entered into between a property owner and a contractor.
          </ListItem>
          <ListItem>
            Physical repairs, renovations, installations, or any other
            construction or maintenance work performed at any property.
          </ListItem>
          <ListItem>
            Workmanship defects, construction defects, property damage, personal
            injury, or financial loss arising from or related to contractor
            services.
          </ListItem>
          <ListItem>
            A contractor's failure to perform, abandonment of work, breach of
            contract, or violation of applicable laws or regulations.
          </ListItem>
          <ListItem>
            Disputes between a property owner and a contractor regarding pricing,
            scope, timeline, payment, or quality of work.
          </ListItem>
        </List>
        <TextSection>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {APP_NAME},{" "}
          {FULL_COMPANY_NAME}, AND THEIR OFFICERS, DIRECTORS, EMPLOYEES, AND
          AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES ARISING FROM OR
          RELATED TO YOUR USE OF THE SERVICES, INCLUDING BUT NOT LIMITED TO
          LOSS OF PROFITS, LOSS OF DATA, PROPERTY DAMAGE, OR PERSONAL INJURY,
          EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER
          SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU TO {APP_NAME} IN THE
          THREE (3) MONTHS PRECEDING THE CLAIM.
        </TextSection>
        <TextSection>
          THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING
          BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
          A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </TextSection>

        {/* ── Section 4 ── */}
        <Header>4. Search Execution Fee: Non-Refundability</Header>
        <TextSection>
          <strong>Fee structure.</strong> {APP_NAME} charges a one-time{" "}
          <strong>$49</strong> flat search execution fee to initiate the
          automated multi-bid contractor dispatch process. The fee is the same
          regardless of project type or scope complexity. It is disclosed to
          the User in the {APP_NAME} AI chat interface before checkout is
          presented, and is confirmed at the time of payment.
        </TextSection>
        <TextSection>
          <strong>What the fee covers.</strong> The search execution fee
          covers the administrative data orchestration, AI-powered scope
          compilation, and automated routing of your pre-scoped project data
          packet across our local contractor notification network. This fee
          compensates for the system resources, outreach infrastructure, and
          platform coordination required to execute the multi-bid search on
          your behalf.
        </TextSection>
        <TextSection>
          <strong>Non-refundability upon transmission.</strong> The search
          execution fee is <strong>non-refundable</strong> once the dispatch
          has been initiated and your project data packet has been transmitted
          to the contractor notification network. Because the fee is charged
          for launching the system search query, not for the outcome of any
          bid or subsequent agreement. No refunds will be issued after
          transmission has begun, regardless of whether you receive bids,
          select a contractor, or proceed with any repair or renovation work.
        </TextSection>
        <TextSection>
          If a technical error on our platform prevents your project data from
          being transmitted, you may contact us at{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a> to
          request a review within 30 days of your payment.
        </TextSection>

        {/* ── Section 5 ── */}
        <Header>5. Contractor Participation Terms</Header>
        <TextSection>
          Independent contractors and tradesmen who receive dispatch
          notifications via the {APP_NAME} network acknowledge and agree that:
        </TextSection>
        <List>
          <ListItem>
            They are independent contractors and not employees, agents,
            partners, or joint-venture participants of {APP_NAME} or{" "}
            {FULL_COMPANY_NAME}.
          </ListItem>
          <ListItem>
            They are solely responsible for maintaining all required licenses,
            permits, insurance (including general liability and workers&apos;
            compensation where required), and bonding applicable to their
            trade and jurisdiction.
          </ListItem>
          <ListItem>
            Any bids, quotes, or proposals submitted in response to a dispatch
            notification constitute a direct offer from the contractor to the
            property owner and do not involve or obligate {APP_NAME}.
          </ListItem>
          <ListItem>
            They will not misrepresent their credentials, scope capabilities,
            or pricing to property owners and will comply with all applicable
            federal, state, and local laws governing their trade.
          </ListItem>
        </List>

        {/* ── Section 6 ── */}
        <Header>6. User Eligibility and Account Registration</Header>
        <TextSection>
          You must be at least 18 years of age and legally capable of entering
          into binding contracts to use the Services. By creating an account,
          you represent and warrant that all information you provide is
          accurate, current, and complete. You are responsible for maintaining
          the confidentiality of your account credentials and for all activity
          that occurs under your account.
        </TextSection>
        <TextSection>
          {APP_NAME} reserves the right to suspend or terminate any account
          that violates these Terms, provides false or misleading information,
          or engages in conduct that is harmful to other Users, contractors,
          or the integrity of the platform.
        </TextSection>

        {/* ── Section 7 ── */}
        <Header>7. Prohibited Conduct</Header>
        <TextSection>
          Users may not use the Services to:
        </TextSection>
        <List>
          <ListItem>
            Submit false, fraudulent, or materially misleading project
            descriptions, property information, or contact details.
          </ListItem>
          <ListItem>
            Circumvent or manipulate the AI intake system to obtain inflated
            scopes, artificially low cost estimates, or unauthorized access
            to contractor contact information.
          </ListItem>
          <ListItem>
            Harass, threaten, defame, or engage in discriminatory conduct
            toward contractors, property owners, or {APP_NAME} personnel.
          </ListItem>
          <ListItem>
            Use automated bots, scrapers, or programmatic methods to access
            the Services without our express written authorization.
          </ListItem>
          <ListItem>
            Violate any applicable federal, state, or local law or regulation,
            including but not limited to fair housing laws, consumer protection
            statutes, and telecommunications regulations.
          </ListItem>
        </List>

        {/* ── Section 8 ── */}
        <Header>8. Intellectual Property</Header>
        <TextSection>
          All content, software, algorithms, AI models, trademarks, service
          marks, logos, and materials made available through the Services are
          the exclusive property of {FULL_COMPANY_NAME} or its licensors and
          are protected by applicable intellectual property laws. You are
          granted a limited, non-exclusive, non-transferable, revocable license
          to access and use the Services solely for their intended purpose
          under these Terms. You may not reproduce, distribute, modify, create
          derivative works of, or commercially exploit any part of the Services
          without our express written consent.
        </TextSection>

        {/* ── Section 9 ── */}
        <Header>9. Indemnification</Header>
        <TextSection>
          You agree to indemnify, defend, and hold harmless {APP_NAME},{" "}
          {FULL_COMPANY_NAME}, and their respective officers, directors,
          employees, agents, and successors from and against any claims,
          liabilities, damages, losses, costs, and expenses (including
          reasonable attorneys&apos; fees) arising out of or in any way
          connected with: (a) your use of the Services; (b) your violation of
          these Terms; (c) your misrepresentation of any information provided
          through the platform; or (d) any dispute between you and a
          contractor or property owner arising from a transaction facilitated
          by the Services.
        </TextSection>

        {/* ── Section 10 ── */}
        <Header>10. Governing Law and Dispute Resolution</Header>
        <TextSection>
          These Terms shall be governed by and construed in accordance with
          the laws of the State of Missouri, without regard to its conflict of
          law provisions. Any dispute arising from or relating to these Terms
          or the Services shall be resolved through binding arbitration in
          St. Louis County, Missouri, in accordance with the rules of the
          American Arbitration Association, except that either party may seek
          injunctive or other equitable relief in a court of competent
          jurisdiction to prevent irreparable harm pending arbitration.
        </TextSection>
        <TextSection>
          You waive any right to participate in a class action lawsuit or
          class-wide arbitration against {APP_NAME} or {FULL_COMPANY_NAME}.
        </TextSection>

        {/* ── Section 11 ── */}
        <Header>11. Modifications to These Terms</Header>
        <TextSection>
          We reserve the right to update or modify these Terms at any time.
          When we make material changes, we will update the "Last Updated"
          date at the top of this page and, where appropriate, provide
          in-app notification. Your continued use of the Services after any
          such changes constitutes your acceptance of the revised Terms.
        </TextSection>

        {/* ── Section 12 ── */}
        <Header>12. Contact Information</Header>
        <TextSection>
          If you have questions about these Terms, please contact us at:{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
          Additional information about our terms and privacy practices is
          available at{" "}
          <a
            href={`https://${APP_URL}${APP_TERMS_ROUTE}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}{APP_TERMS_ROUTE}
          </a>{" "}
          and{" "}
          <a
            href={`https://${APP_URL}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}/privacy
          </a>
          .
        </TextSection>

      </TextContainer>
    </Container>
  );
}

const Container = styled.div`
  color: black;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  text-align: left;
  width: 80%;
  margin: auto;
  margin-bottom: ${(props) => (props.$mustAcceptTos ? "0" : "40")}px;
  margin-top: 3rem;

  a {
    color: #1a73e8;
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: #0d47a1;
    text-decoration: underline;
  }
`;

const TextContainer = styled.div`
  overflow-y: ${(props) => (props.$mustAcceptTos ? "auto" : "unset")};
  overflow-x: ${(props) => (props.$mustAcceptTos ? "hidden" : "unset")};
  height: ${(props) => (props.$mustAcceptTos ? "500px" : "auto")};
  max-height: ${(props) => (props.$mustAcceptTos ? "500px" : "auto")};
  box-shadow: ${(props) =>
    props.$mustAcceptTos ? "0px 4px 0px 0px rgba(0, 0, 0, 0.1)" : "none"};
  padding-right: 5%;
  padding-bottom: 32px;

  @media screen and (max-height: 825px) {
    height: ${(props) => (props.$mustAcceptTos ? "400px" : "auto")};
    max-height: ${(props) => (props.$mustAcceptTos ? "400px" : "auto")};
  }
`;

const Header = styled.div`
  font-size: 13px;
  font-weight: bold;
  margin-top: 24px;
`;

const MainHeader = styled.div`
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 18px;
`;

const List = styled.ul`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 18px;
`;

const ListItem = styled.li`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 10px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;

const TextSection = styled.div`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 18px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;
